import { Component, Inject, OnInit, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import {
    CompaniesService, CompanyListDto, DashboardShareCreateDto, DashboardShareLevel, DashboardShareListDto, DashboardSharesService,
    UsersService, UserListDto
} from "@common/generated";
import { SnackbarService } from "@common/services/snackbar.service";
import { catchError, forkJoin, of } from "rxjs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatRadioModule } from "@angular/material/radio";
interface DashboardShareDialogData {
    dashboardId: string;
}

type ShareTargetType = "company" | "user";

interface ShareTargetOption {
    id: string;
    label: string;
    secondaryLabel: string;
    type: ShareTargetType;
}

@Component({
    selector: "app-dashboard-share-dialog",
    standalone: true,
    imports: [ReactiveFormsModule, TranslocoModule, MatDialogModule, MatTooltipModule, MatRadioModule, MatAutocompleteModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, MatSelectModule, MatTableModule],
    templateUrl: "./dashboard-share-dialog.component.html",
    styleUrls: ["./dashboard-share-dialog.component.scss"]
})
export class DashboardShareDialogComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<DashboardShareDialogComponent>);
    private readonly dashboardSharesService = inject(DashboardSharesService);
    private readonly companiesService = inject(CompaniesService);
    private readonly usersService = inject(UsersService);
    private readonly translocoService = inject(TranslocoService);
    private readonly snackbarService = inject(SnackbarService)

    public readonly DashboardShareLevel = DashboardShareLevel;

    public readonly shareForm: FormGroup = this.fb.group({
        level: [DashboardShareLevel.Cibling, Validators.required],
        companyIds: [[] as string[]],
        userIds: [[] as string[]],
        canEdit: [false],
        canDelete: [false],
        canShare: [false]
    });

    public currentShares: DashboardShareListDto[] = [];
    public companies: CompanyListDto[] = [];
    public users: UserListDto[] = [];
    public loading = false;
    public readonly shareTargetSearchControl = new FormControl<string | ShareTargetOption>("");

    public ciblingUserIds = toSignal(this.shareForm.get('userIds')!.valueChanges, { initialValue: [] as string[] });
    public ciblingCompanyIds = toSignal(this.shareForm.get('companyIds')!.valueChanges, { initialValue: [] as string[] });

    public ciblingUsers = computed(() => {
        const userIds = this.ciblingUserIds();
        return this.users.filter(user => userIds.includes(user.id));
    });

    public ciblingCompanies = computed(() => {
        const companyIds = this.ciblingCompanyIds();
        return this.companies.filter(company => companyIds.includes(company.id));
    });

    constructor(@Inject(MAT_DIALOG_DATA) public data: DashboardShareDialogData) { }

    ngOnInit(): void {
        this.loadData();
    }

    public onCancel(): void {
        this.dialogRef.close();
    }

    public onSubmit(): void {
        if (!this.canSubmit()) {
            return;
        }

        const level = this.shareForm.get("level")?.value as DashboardShareLevel;
        const companyIds = (this.shareForm.get("companyIds")?.value as string[]) ?? [];
        const userIds = (this.shareForm.get("userIds")?.value as string[]) ?? [];

        const payload: DashboardShareCreateDto = {
            dashboardId: this.data.dashboardId,
            level,
            companyIds: this.isCiblingLevel() ? companyIds : null,
            userIds: this.isCiblingLevel() ? userIds : null,
            canEdit: !!this.shareForm.get("canEdit")?.value,
            canDelete: !!this.shareForm.get("canDelete")?.value,
            canShare: !!this.shareForm.get("canShare")?.value
        };

        this.dashboardSharesService.shareDashboard(payload).subscribe({
            next: () => {
                this.shareForm.patchValue({ companyIds: [], userIds: [] });
                this.loadData();
            },
            error: () => {
                this.snackbarService.error('dashboard-share-dialog.share-error');
            }
        });
    }

    public isCiblingLevel(): boolean {
        return this.shareForm.get("level")?.value === DashboardShareLevel.Cibling;
    }

    public canSubmit(): boolean {
        if (this.shareForm.invalid) {
            return false;
        }

        if (!this.isCiblingLevel()) {
            return true;
        }

        const companyIds = (this.shareForm.get("companyIds")?.value as string[]) ?? [];
        const userIds = (this.shareForm.get("userIds")?.value as string[]) ?? [];

        return companyIds.length > 0 || userIds.length > 0;
    }

    public getShareTargetLabel(share: DashboardShareListDto): string {
        if (share.isSharedWithCompany) {
            return share.companyName || "-";
        }

        if (share.isSharedWithUser) {
            return share.userFullName || "-";
        }

        if (share.isSharedWithSubscribers) {
            return this.translocoService.translate("dashboard-share-dialog.levels.subscribers");
        }

        if (share.isSharedPublicly) {
            return this.translocoService.translate("dashboard-share-dialog.levels.public");
        }

        return "-";
    }

    public getShareTypeLabel(share: DashboardShareListDto): string {
        if (share.isSharedWithCompany) {
            return this.translocoService.translate("dashboard-share-dialog.option.company");
        }

        if (share.isSharedWithUser) {
            return this.translocoService.translate("dashboard-share-dialog.option.user");
        }
        return this.translocoService.translate("dashboard-share-dialog.option.global");
    }

    public getSharePermissionLabel(share: DashboardShareListDto): string {
        const permissions: string[] = [];

        if (share.canEdit) {
            permissions.push(this.translocoService.translate("dashboard-share-dialog.permission-options.edit"));
        }

        if (share.canDelete) {
            permissions.push(this.translocoService.translate("dashboard-share-dialog.permission-options.delete"));
        }

        if (share.canShare) {
            permissions.push(this.translocoService.translate("dashboard-share-dialog.permission-options.share"));
        }

        return permissions.length > 0
            ? permissions.join(" • ")
            : this.translocoService.translate("dashboard-share-dialog.permission-options.read-only");
    }

    public readonly displayShareTarget = (value: string | ShareTargetOption | null): string => {
        if (!value) {
            return "";
        }

        return typeof value === "string" ? value : value.label;
    };

    public getFilteredShareTargets(): ShareTargetOption[] {
        const rawValue = this.shareTargetSearchControl.value;
        const searchTerm = (typeof rawValue === "string" ? rawValue : rawValue?.label || "").trim().toLowerCase();

        const selectedCompanyIds = ((this.shareForm.get("companyIds")?.value as string[]) ?? []).filter(Boolean);
        const selectedUserIds = ((this.shareForm.get("userIds")?.value as string[]) ?? []).filter(Boolean);

        const sharedCompanyIds = new Set(
            this.currentShares
                .map(share => share.companyId)
                .filter((id): id is string => !!id)
        );

        const sharedUserIds = new Set(
            this.currentShares
                .map(share => share.userId)
                .filter((id): id is string => !!id)
        );

        const companyOptions: ShareTargetOption[] = this.companies
            .filter(company => {
                const companyId = company.id || "";
                const companyName = company.name || "";

                if (!companyId || selectedCompanyIds.includes(companyId) || sharedCompanyIds.has(companyId)) {
                    return false;
                }

                if (!searchTerm) {
                    return false;
                }

                return companyName.toLowerCase().includes(searchTerm);
            })
            .map(company => ({
                id: company.id!,
                label: company.name || "-",
                secondaryLabel: this.translocoService.translate("dashboard-share-dialog.option.company"),
                type: "company"
            }));

        const userOptions: ShareTargetOption[] = this.users
            .filter(user => {
                const userId = user.id || "";
                const fullName = user.fullName || "";
                const email = user.email || "";

                if (!userId || selectedUserIds.includes(userId) || sharedUserIds.has(userId)) {
                    return false;
                }

                if (!searchTerm) {
                    return false;
                }

                return fullName.toLowerCase().includes(searchTerm) || email.toLowerCase().includes(searchTerm);
            })
            .map(user => ({
                id: user.id!,
                label: user.fullName || user.email || "-",
                secondaryLabel: user.email || this.translocoService.translate("dashboard-share-dialog.option.user"),
                type: "user"
            }));

        return [...companyOptions, ...userOptions]
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(0, 5); // Limiter à 5 résultats pour éviter de surcharger l'interface
    }

    public onShareTargetSelected(option: ShareTargetOption): void {
        if (!option?.id) {
            return;
        }

        if (option.type === "company") {
            const currentCompanyIds = ((this.shareForm.get("companyIds")?.value as string[]) ?? []).filter(Boolean);
            if (!currentCompanyIds.includes(option.id)) {
                this.shareForm.get("companyIds")?.setValue([...currentCompanyIds, option.id]);
            }
        }

        if (option.type === "user") {
            const currentUserIds = ((this.shareForm.get("userIds")?.value as string[]) ?? []).filter(Boolean);
            if (!currentUserIds.includes(option.id)) {
                this.shareForm.get("userIds")?.setValue([...currentUserIds, option.id]);
            }
        }

        this.shareTargetSearchControl.setValue("");
    }

    public onShareTargetOptionSelected(option: ShareTargetOption): void {
        this.onShareTargetSelected(option);
    }

    public onShareTargetOptionMouseDown(event: MouseEvent, option: ShareTargetOption): void {
        event.preventDefault();
        this.onShareTargetSelected(option);
    }

    onRemoveShare(share: DashboardShareListDto): void {
        console.log("Removing share with ID:", share.id);
        this.dashboardSharesService.removeDashboardShare(share.id!).subscribe({
            next: () => {
                this.loadData();
                this.snackbarService.success('dashboard-share-dialog.unshare-success');
            },
            error: () => {
                this.snackbarService.error('dashboard-share-dialog.unshare-error');
            }
        });
    }

    private loadData(): void {
        this.loading = true;

        forkJoin({
            shares: this.dashboardSharesService.getDashboardShares(this.data.dashboardId).pipe(catchError(() => of([]))),
            companies: this.companiesService.getAllCompanies().pipe(catchError(() => of([]))),
            users: this.usersService.getAllUsers().pipe(catchError(() => of([])))
        }).subscribe({
            next: ({ shares, companies, users }) => {
                this.currentShares = shares;
                this.companies = companies;
                this.users = users;
                this.loading = false;
            },
            error: () => {
                this.currentShares = [];
                this.companies = [];
                this.users = [];
                this.loading = false;
            }
        });
    }

    removeCiblingCompany(companyId: string): void {
        console.log("Removing company with ID:", companyId);
        const currentCompanyIds = ((this.shareForm.get("companyIds")?.value as string[]) ?? []).filter(Boolean);
        this.shareForm.get("companyIds")?.setValue(currentCompanyIds.filter(id => id !== companyId));
    }

    public onRemoveCiblingCompany(event: MouseEvent, companyId: string): void {
        event.preventDefault();
        event.stopPropagation();
        this.removeCiblingCompany(companyId);
    }

    removeCiblingUser(userId: string): void {
        console.log("Removing user with ID:", userId);
        const currentUserIds = ((this.shareForm.get("userIds")?.value as string[]) ?? []).filter(Boolean);
        this.shareForm.get("userIds")?.setValue(currentUserIds.filter(id => id !== userId));
    }

    public onRemoveCiblingUser(event: MouseEvent, userId: string): void {
        event.preventDefault();
        event.stopPropagation();
        this.removeCiblingUser(userId);
    }
}
